import React, { createContext, useContext, useEffect, useState } from 'react';

export interface ViewportSizeInfo {
  width: number;
  height: number;
  availableHeight: number; // Height minus fixed elements
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  canFitPanels: boolean; // Based on minimum panel requirements
  recommendedLayout: 'stacked' | 'tabbed' | 'sidebar' | 'modal-only';
}

interface ViewportSizeContextType {
  viewport: ViewportSizeInfo;
  registerFixedElement: (id: string, height: number) => void;
  unregisterFixedElement: (id: string) => void;
  getMinimumSpaceRequired: (areas: string[]) => number;
  canFitInViewport: (areas: string[], requiredSpace: number) => boolean;
}

const ViewportSizeContext = createContext<ViewportSizeContextType | null>(null);

// Minimum heights for different panel areas (in pixels)
export const MINIMUM_PANEL_HEIGHTS = {
  HEADER: 60,
  TABS: 48,
  SECONDARY_TABS: 44,
  ACTION_BUTTONS: 52,
  FOOTER: 40,
  BREADCRUMB: 32,
  SUBHEADER: 48,
  CONTENT: 200, // Minimum content area height
  LEFT_SIDEBAR: 150,
  RIGHT_SIDEBAR: 150,
  BOTTOM_PANEL: 120,
  TOP_PANEL: 100,
} as const;

// Breakpoints for responsive behavior
const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1440,
} as const;

export const ViewportSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [viewport, setViewport] = useState<ViewportSizeInfo>({
    width: window.innerWidth,
    height: window.innerHeight,
    availableHeight: window.innerHeight,
    isMobile: window.innerWidth < BREAKPOINTS.mobile,
    isTablet: window.innerWidth >= BREAKPOINTS.mobile && window.innerWidth < BREAKPOINTS.desktop,
    isDesktop: window.innerWidth >= BREAKPOINTS.desktop,
    canFitPanels: true,
    recommendedLayout: 'sidebar',
  });

  const [fixedElements, setFixedElements] = useState<Map<string, number>>(new Map());

  const calculateAvailableHeight = (fixedHeights: Map<string, number>) => {
    const totalFixedHeight = Array.from(fixedHeights.values()).reduce((sum, height) => sum + height, 0);
    return Math.max(window.innerHeight - totalFixedHeight - 20, 300); // 20px buffer, minimum 300px
  };

  const determineRecommendedLayout = (width: number, availableHeight: number): ViewportSizeInfo['recommendedLayout'] => {
    if (width < BREAKPOINTS.mobile) {
      return availableHeight < 500 ? 'modal-only' : 'stacked';
    }
    if (width < BREAKPOINTS.tablet) {
      return availableHeight < 400 ? 'tabbed' : 'stacked';
    }
    return availableHeight < 600 ? 'tabbed' : 'sidebar';
  };

  const updateViewport = () => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const availableHeight = calculateAvailableHeight(fixedElements);
    const isMobile = width < BREAKPOINTS.mobile;
    const isTablet = width >= BREAKPOINTS.mobile && width < BREAKPOINTS.desktop;
    const isDesktop = width >= BREAKPOINTS.desktop;

    // Estimate if we can fit basic panel layout
    const minRequiredSpace = MINIMUM_PANEL_HEIGHTS.HEADER + MINIMUM_PANEL_HEIGHTS.TABS + MINIMUM_PANEL_HEIGHTS.CONTENT + MINIMUM_PANEL_HEIGHTS.FOOTER;

    const canFitPanels = availableHeight >= minRequiredSpace;
    const recommendedLayout = determineRecommendedLayout(width, availableHeight);

    setViewport({
      width,
      height,
      availableHeight,
      isMobile,
      isTablet,
      isDesktop,
      canFitPanels,
      recommendedLayout,
    });
  };

  useEffect(() => {
    updateViewport();

    const handleResize = () => {
      updateViewport();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [fixedElements]);

  const registerFixedElement = (id: string, height: number) => {
    setFixedElements((prev) => {
      const next = new Map(prev);
      next.set(id, height);
      return next;
    });
  };

  const unregisterFixedElement = (id: string) => {
    setFixedElements((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };

  const getMinimumSpaceRequired = (areas: string[]): number => {
    return areas.reduce((total, area) => {
      const height = MINIMUM_PANEL_HEIGHTS[area as keyof typeof MINIMUM_PANEL_HEIGHTS] || 50;
      return total + height;
    }, 0);
  };

  const canFitInViewport = (areas: string[], requiredSpace: number): boolean => {
    const minSpace = getMinimumSpaceRequired(areas);
    return viewport.availableHeight >= Math.max(minSpace, requiredSpace);
  };

  return (
    <ViewportSizeContext.Provider
      value={{
        viewport,
        registerFixedElement,
        unregisterFixedElement,
        getMinimumSpaceRequired,
        canFitInViewport,
      }}
    >
      {children}
    </ViewportSizeContext.Provider>
  );
};

export const useViewportSize = (): ViewportSizeContextType => {
  const context = useContext(ViewportSizeContext);
  if (!context) {
    throw new Error('useViewportSize must be used within a ViewportSizeProvider');
  }
  return context;
};

// Hook for registering fixed height elements
export const useFixedHeight = (id: string, height: number) => {
  const { registerFixedElement, unregisterFixedElement } = useViewportSize();

  useEffect(() => {
    registerFixedElement(id, height);
    return () => unregisterFixedElement(id);
  }, [id, height, registerFixedElement, unregisterFixedElement]);
};
